import pino, { LogFn, Logger as PinoLogger } from 'pino';
import { v4 as uuidv4 } from 'uuid';
import { LogLevel, LogLevelPriority } from '../../../constants';
import { Log } from '../../../types';
import {
  FormattedLogEntry,
  LevelService,
  LogEntry,
  LoggerAdapter,
  LoggerMetadata,
} from '../../types';
import { ConsoleLogger } from '../loggers/console';
import { HttpLogger } from '../loggers/http.logger';
import { InvalidLogLevelError } from '../utils';

const isProd = import.meta.env.PROD;

interface PinoAdapterConfig {
  source: string;
  levelService: LevelService;
  consoleLogger: ConsoleLogger;
  httpLogger: HttpLogger;
  metadata?: LoggerMetadata;
}

/**
 * PinoAdapter is a LoggerAdapter implementation using the pino logging library.
 *
 * Global metadata is stored in a static variable, so any updates via setMetadata
 * affect all log entries across all adapter instances.
 */
export class PinoAdapter implements LoggerAdapter {
  // Global metadata shared across all instances.
  private static globalMetadata: LoggerMetadata = {};
  private static root: PinoLogger<LogLevel> | null = null;

  private instance: PinoLogger<LogLevel>;
  private httpLogger: HttpLogger;
  private consoleLogger: ConsoleLogger;
  private config: PinoAdapterConfig;

  /**
   * Creates a new instance of PinoAdapter.
   * @param config - The adapter configuration.
   */
  constructor(config: PinoAdapterConfig) {
    this.config = config;
    const {
      source,
      levelService,
      metadata = {},
      consoleLogger,
      httpLogger,
    } = config;

    // Initialize the root logger only once.
    PinoAdapter.root ??= PinoAdapter.createRootLogger(
      consoleLogger,
      httpLogger,
    );

    // Merge provided metadata into the global metadata.
    PinoAdapter.mergeGlobalMetadata(metadata);

    this.instance = PinoAdapter.root.child({ source });
    this.consoleLogger = consoleLogger;
    this.httpLogger = httpLogger;
    this.setLevel(levelService.getLevel(source));
  }

  /**
   * Creates the root pino logger with custom options.
   * @param source - The source context.
   * @param httpLogger - The HTTP logger for production logs.
   * @returns A root pino logger instance.
   */
  private static createRootLogger(
    consoleLogger: ConsoleLogger,
    httpLogger: HttpLogger,
  ): PinoLogger<LogLevel> {
    return pino<LogLevel>({
      customLevels: LogLevelPriority,
      browser: {
        write: (obj: unknown) => {
          const { payload, options, stack, ...rest } = obj as FormattedLogEntry;
          const log = { ...rest, ...payload } as Log;

          if (!options.silent) {
            consoleLogger.print(log, stack);
          }
          if (isProd && log.level >= LogLevelPriority[LogLevel.WARN]) {
            httpLogger.save(log);
          }
        },
      },
    });
  }

  /**
   * Merges new metadata into the global metadata.
   * @param metadata - The metadata to merge.
   */
  private static mergeGlobalMetadata(metadata: LoggerMetadata = {}): void {
    PinoAdapter.globalMetadata = { ...PinoAdapter.globalMetadata, ...metadata };
  }

  /**
   * Creates a child logger with an extended source context.
   * Any additional metadata provided is merged into the global metadata.
   * @param source - The additional source context.
   * @param metadata - Optional metadata to merge globally.
   * @returns A new PinoAdapter instance representing the child logger.
   */
  public child(source: string, metadata: LoggerMetadata = {}): LoggerAdapter {
    PinoAdapter.mergeGlobalMetadata(metadata);
    return new PinoAdapter({
      source: `${this.config.source}:${source}`,
      levelService: this.config.levelService,
      consoleLogger: this.consoleLogger,
      httpLogger: this.httpLogger,
    });
  }

  /**
   * Sets the logging level for this adapter.
   * @param level - The desired log level.
   */
  public setLevel(level: LogLevel): void {
    this.instance.level = level;
  }

  /**
   * Updates the global metadata. All subsequent log entries will include the updated metadata.
   * @param metadata - The metadata to merge into the global metadata.
   */
  public setMetadata(metadata: LoggerMetadata): void {
    PinoAdapter.mergeGlobalMetadata(metadata);
  }

  /**
   * Formats a log entry into a structured log payload.
   * @param entry - The log entry to format.
   * @returns A formatted log entry.
   */
  private formatLogEntry(entry: LogEntry): FormattedLogEntry {
    const { content, stack, options = {} } = entry;
    const { message, args = [] } = content;
    const id = uuidv4();
    const data: Record<string, any> = {};
    let msg: string;

    if (message instanceof Error) {
      data.error = {
        name: message.name,
        message: message.message,
        stack: message.stack,
      };
      msg = message.message;
    } else {
      msg =
        typeof message === 'object' ? JSON.stringify(message) : String(message);
    }

    args.forEach((arg, idx) => this.formatArg(arg, idx, data));

    const payload = { id, msg, data, ...PinoAdapter.globalMetadata };
    return { payload, options, stack };
  }

  /**
   * Formats an individual argument and merges it into the data object.
   * @param arg - The argument to format.
   * @param idx - The index of the argument.
   * @param data - The accumulator for formatted data.
   */
  private formatArg(arg: any, idx: number, data: Record<string, any>): void {
    if (arg instanceof Error) {
      const key = data.error ? `error${idx + 1}` : 'error';
      data[key] = { message: arg.message, stack: arg.stack, name: arg.name };
    } else if (typeof arg === 'object' && arg !== null) {
      Object.assign(data, arg);
    } else {
      data[`$${idx}`] = String(arg);
    }
  }

  /**
   * Logs an entry at the specified level.
   * @param level - The log level method to call.
   * @param entry - The log entry.
   * @throws Will throw an error if the log level method is not supported.
   */
  private log(level: keyof PinoLogger, entry: LogEntry): void {
    if (typeof this.instance[level] !== 'function') {
      throw new InvalidLogLevelError(level);
    }
    const formatted = this.formatLogEntry(entry);
    (this.instance[level] as LogFn)(formatted);
  }

  public trace(entry: LogEntry): void {
    this.log('trace', entry);
  }

  public debug(entry: LogEntry): void {
    this.log('debug', entry);
  }

  public info(entry: LogEntry): void {
    this.log('info', entry);
  }

  public warn(entry: LogEntry): void {
    this.log('warn', entry);
  }

  public error(entry: LogEntry): void {
    this.log('error', entry);
  }

  public fatal(entry: LogEntry): void {
    this.log('fatal', entry);
  }

  get source(): string {
    return this.config.source;
  }
}
